import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../../lib/api';
import {
  getAdminToken, setAdminToken, removeAdminToken, clearAdminSession,
} from '../../lib/tokenStorage';

const AdminAuthContext = createContext(null);

const normalizeAdmin = (adminData) => {
  if (!adminData || typeof adminData !== 'object' || Array.isArray(adminData)) {
    return null;
  }

  return {
    ...adminData,
    permissions: Array.isArray(adminData.permissions)
      ? adminData.permissions.filter((permission) => typeof permission === 'string')
      : [],
  };
};

export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requirePasswordChange, setRequirePasswordChange] = useState(false);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = getAdminToken();
    if (!token) {
      setAuthError(null);
      setLoading(false);
      return;
    }

    try {
      const response = await api.adminGetMe(token);
      const adminData = normalizeAdmin(response.data);
      if (!adminData) {
        throw new Error('Admin session response was invalid.');
      }
      setAdmin(adminData);
      setRequirePasswordChange(Boolean(adminData.require_password_change));
      setAuthError(null);
    } catch (error) {
      clearAdminSession();
      setAdmin(null);
      setAuthError(
        error?.response?.status === 403
          ? 'Admin access required.'
          : 'Admin sign-in could not be verified.'
      );
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    const response = await api.adminLogin(username, password);
    const { access_token, admin: adminData, require_password_change } = response.data;
    const normalizedAdmin = normalizeAdmin(adminData);
    if (!access_token || !normalizedAdmin) {
      throw new Error('Admin login response was invalid.');
    }
    
    setAdminToken(access_token);
    setAdmin(normalizedAdmin);
    setRequirePasswordChange(Boolean(require_password_change));
    setAuthError(null);
    
    return { requirePasswordChange: require_password_change };
  };

  const logout = async () => {
    try {
      const token = getAdminToken();
      if (token) {
        await api.adminLogout(token);
      }
    } catch (error) {
      // Logout API failure is non-critical
    } finally {
      clearAdminSession();
      setAdmin(null);
      setAuthError(null);
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    const token = getAdminToken();
    await api.adminChangePassword(token, currentPassword, newPassword);
    setRequirePasswordChange(false);
    setAdmin(prev => ({ ...prev, require_password_change: false }));
  };

  const hasPermission = (permission) => {
    if (!permission) return true;
    if (!admin) return false;
    const permissions = Array.isArray(admin.permissions) ? admin.permissions : [];
    return permissions.includes(permission) || permissions.includes('manage_admin_users');
  };

  const value = {
    admin,
    authError,
    loading,
    requirePasswordChange,
    login,
    logout,
    changePassword,
    hasPermission,
    isAuthenticated: !!admin,
    isSuperAdmin: admin?.role === 'super_admin',
    token: getAdminToken()
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
}
